/**
 * Ability Registry
 *
 * Player-activated abilities with cooldowns, stacking, and categories.
 * Unlike Powerups (passive collectibles), Abilities are:
 * - Actively triggered by the player (keybind)
 * - Equipped in category-specific slots
 * - Stackable for enhanced effects
 * - Subject to individual + global cooldowns
 *
 * Categories:
 * - UTILITY: Invisibility, Invulnerability, etc.
 * - MOVEMENT: Teleport, Dash, etc.
 */

import { getSharedLogger } from './utils/loggerProxy.js';

const abilityLogger = getSharedLogger('ABILITY');

// ─────────────────────────────────────────────────────────────
// CONCRETE ABILITY FACTORY (server installs; client leaves null)
// ─────────────────────────────────────────────────────────────
let abilityFactory = null;

/**
 * Install the function that turns an ability config into a concrete Ability
 * subclass. The server calls this from src/server/runtime/registries.js with
 * src/server/abilities' createAbility; shared/ must not import server code.
 * @param {((config: object) => Ability) | null} fn
 */
export function setAbilityFactory(fn) {
  abilityFactory = typeof fn === 'function' ? fn : null;
}

  // ─────────────────────────────────────────────────────────────
  // ABILITY CATEGORIES
  // ─────────────────────────────────────────────────────────────
  const AbilityCategory = {
    UTILITY: 'utility',
    MOVEMENT: 'movement'
  };

  // Slot configuration (which key activates which category)
  const AbilitySlots = {
    [AbilityCategory.UTILITY]: { index: 0, key: 'Q', keyCode: 81 },
    [AbilityCategory.MOVEMENT]: { index: 1, key: 'E', keyCode: 69 }
  };

  // ─────────────────────────────────────────────────────────────
  // ABILITY RARITY
  // ─────────────────────────────────────────────────────────────
  const AbilityRarity = {
    COMMON: 'common',
    UNCOMMON: 'uncommon',
    RARE: 'rare',
    EPIC: 'epic',
    LEGENDARY: 'legendary'
  };

  const RarityColors = {
    [AbilityRarity.COMMON]: '#9d9d9d',
    [AbilityRarity.UNCOMMON]: '#1eff00',
    [AbilityRarity.RARE]: '#0070dd',
    [AbilityRarity.EPIC]: '#a335ee',
    [AbilityRarity.LEGENDARY]: '#ff8000'
  };

  // ─────────────────────────────────────────────────────────────
  // BASE ABILITY CLASS
  // ─────────────────────────────────────────────────────────────
  class Ability {
    constructor(config) {
      this.id = config.id;
      this.name = config.name;
      this.description = config.description || '';
      this.category = config.category;
      this.rarity = config.rarity || AbilityRarity.COMMON;

      // Visual
      this.icon = config.icon || 'assets/art/ui/genericAbilityIcon.png';
      this.color = config.color || '#ffffff';

      // Cooldown (ms)
      this.cooldown = config.cooldown || 10000;

      // Duration (ms) - 0 for instant abilities
      this.duration = config.duration || 0;

      // Gameplay restrictions
      this.canShoot = config.canShoot !== undefined ? config.canShoot : true;
      this.canReload = config.canReload !== undefined ? config.canReload : true;

      // Stacking
      this.maxStacks = config.maxStacks || 4;
      this.stackBonuses = config.stackBonuses || {};
      // Example stackBonuses: { cooldownReduction: 0.1, durationIncrease: 500 }
      // Per-stack bonus applied

      // Effects (ability-specific data)
      this.effects = config.effects || {};

      // Loot
      this.spawnWeight = config.spawnWeight || 10;
      this.lootSources = config.lootSources || ['chest', 'ground'];

      // Sound: event -> path string or { path, volume } (volume 0–1, default 1)
      this.sounds = config.sounds || {};

      this.metadata = config.metadata || {};
    }

    /**
     * Get cooldown at a specific stack level
     * @param {number} stacks - Current stack level (1-4)
     * @returns {number} - Cooldown in ms
     */
    getCooldownAtStack(stacks) {
      const reduction = this.stackBonuses.cooldownReduction || 0;
      const totalReduction = reduction * (stacks - 1);
      return Math.max(this.cooldown * (1 - totalReduction), this.cooldown * 0.25);
    }

    /**
     * Get duration at a specific stack level
     * @param {number} stacks - Current stack level (1-4)
     * @returns {number} - Duration in ms
     */
    getDurationAtStack(stacks) {
      const increase = this.stackBonuses.durationIncrease || 0;
      return this.duration + (increase * (stacks - 1));
    }

    /**
     * Check if ability can be activated
     * @param {Object} context - { player, room, timestamp, cooldownEnd }
     * @returns {Object} - { canActivate: boolean, reason?: string }
     */
    canActivate(context) {
      const { timestamp, cooldownEnd } = context;

      if (cooldownEnd && timestamp < cooldownEnd) {
        const remaining = Math.ceil((cooldownEnd - timestamp) / 1000);
        return { canActivate: false, reason: `On cooldown (${remaining}s)` };
      }

      return { canActivate: true };
    }

    /**
     * Called when ability is activated
     * Override in subclasses for specific behavior
     * @param {Object} context - { player, room, timestamp, stacks }
     * @returns {Object} - { success: boolean, effects?: Object, message?: string }
     */
    onActivate(context) {
      return { success: true };
    }

    /**
     * Called when ability effect ends (for timed abilities)
     * @param {Object} context - { player, room, timestamp }
     */
    onDeactivate(context) {
      // Override in subclass
    }

    /**
     * Called when ability is interrupted (e.g., taking damage while invisible)
     * @param {Object} context - { player, room, timestamp, reason }
     */
    onInterrupt(context) {
      // Override in subclass
    }

    /**
     * Serialize for network transmission
     */
    serialize() {
      return {
        id: this.id,
        name: this.name,
        category: this.category,
        rarity: this.rarity,
        icon: this.icon,
        color: this.color,
        cooldown: this.cooldown,
        duration: this.duration,
        maxStacks: this.maxStacks
      };
    }

    /**
     * Serialize for UI display
     */
    serializeForUI() {
      return {
        ...this.serialize(),
        description: this.description,
        stackBonuses: this.stackBonuses,
        effects: this.effects,
        sounds: this.sounds
      };
    }
  }

  // ─────────────────────────────────────────────────────────────
  // PLAYER ABILITY STATE (equipped abilities per player)
  // ─────────────────────────────────────────────────────────────
  class PlayerAbilityState {
    constructor() {
      // Equipped abilities by category: { utility: { abilityId, stacks }, movement: { ... } }
      this.equipped = {
        [AbilityCategory.UTILITY]: null,
        [AbilityCategory.MOVEMENT]: null
      };

      // Cooldown end times by ability ID
      this.cooldowns = new Map();

      // Global cooldown end times per category
      this.globalCooldowns = {
        [AbilityCategory.UTILITY]: 0,
        [AbilityCategory.MOVEMENT]: 0
      };

      // Active effects (timed abilities currently running)
      this.activeEffects = new Map(); // abilityId -> { startTime, endTime, stacks }

      this.silencedUntil = 0;
    }

    /**
     * Equip an ability to its category slot
     * @param {string} abilityId
     * @param {string} category
     * @returns {Object} - { success, stacks, rejected, message }
     */
    equipAbility(abilityId, category) {
      const slot = this.equipped[category];

      if (!slot) {
        // Empty slot - equip at stack 1
        this.equipped[category] = { abilityId, stacks: 1 };
        return { success: true, stacks: 1, message: `Equipped ${abilityId}` };
      }

      if (slot.abilityId === abilityId) {
        // Same ability - try to stack up to cap
        if (slot.stacks >= 4) {
          return {
            success: true,
            stacks: slot.stacks,
            capped: true,
            message: 'Max stacks reached'
          };
        }
        slot.stacks++;
        return { success: true, stacks: slot.stacks, message: `Stacked ${abilityId} (x${slot.stacks})` };
      }

      // Different ability in slot - reject
      return {
        success: false,
        rejected: true,
        message: `Slot occupied by ${slot.abilityId}`
      };
    }

    /**
     * Get equipped ability for a category
     * @param {string} category
     * @returns {Object|null} - { abilityId, stacks } or null
     */
    getEquipped(category) {
      return this.equipped[category];
    }

    /**
     * Check if ability can be activated (cooldown check)
     * @param {string} abilityId
     * @param {number} timestamp
     * @param {number} globalCooldown - Global cooldown duration (0 to disable)
     * @returns {Object} - { canActivate, reason }
     */
    canActivate(abilityId, category = null, timestamp, globalCooldown = 0) {
      if (this.silencedUntil && timestamp < this.silencedUntil) {
        const remaining = Math.ceil((this.silencedUntil - timestamp) / 1000);
        return { canActivate: false, reason: `Abilities nullified (${remaining}s)` };
      }

      // Check global cooldown (per slot)
      if (globalCooldown > 0) {
        if (category) {
          const categoryEnd = this.globalCooldowns?.[category] || 0;
          if (timestamp < categoryEnd) {
            const remaining = Math.ceil((categoryEnd - timestamp) / 1000);
            return { canActivate: false, reason: `Global cooldown (${remaining}s)` };
          }
        } else {
          const maxEnd = this.getMaxGlobalCooldownEnd();
          if (timestamp < maxEnd) {
            const remaining = Math.ceil((maxEnd - timestamp) / 1000);
            return { canActivate: false, reason: `Global cooldown (${remaining}s)` };
          }
        }
      }

      // Check ability-specific cooldown
      const cooldownEnd = this.cooldowns.get(abilityId) || 0;
      if (timestamp < cooldownEnd) {
        const remaining = Math.ceil((cooldownEnd - timestamp) / 1000);
        return { canActivate: false, reason: `On cooldown (${remaining}s)` };
      }

      // Prevent activating if any other ability effect is currently active
      for (const [activeId, effect] of this.activeEffects) {
        if (activeId === abilityId) continue;
        if (timestamp < effect.endTime) {
          const remaining = Math.ceil((effect.endTime - timestamp) / 1000);
          return { canActivate: false, reason: `Cannot activate, only one ability can be active at a time` };
        }
      }

      return { canActivate: true };
    }

    /**
     * Start cooldown for an ability
     * @param {string} abilityId
     * @param {number} cooldownMs
     * @param {number} timestamp
     * @param {number} globalCooldown - Global cooldown to apply (0 to disable)
     */
    startCooldown(abilityId, cooldownMs, timestamp, globalCooldown = 0) {
      this.cooldowns.set(abilityId, timestamp + cooldownMs);

      if (globalCooldown > 0) {
        const endTime = timestamp + globalCooldown;
        this.setGlobalCooldownForAll(endTime);
      }
    }

    applySilence(timestamp, durationMs) {
      const until = timestamp + durationMs;
      this.silencedUntil = Math.max(this.silencedUntil || 0, until);
      return this.silencedUntil;
    }

    clearSilence() {
      this.silencedUntil = 0;
    }

    setGlobalCooldownForCategory(category, endTime) {
      if (!category) return;
      if (!this.globalCooldowns) {
        this.globalCooldowns = {
          [AbilityCategory.UTILITY]: 0,
          [AbilityCategory.MOVEMENT]: 0
        };
      }
      if (this.globalCooldowns.hasOwnProperty(category)) {
        this.globalCooldowns[category] = endTime;
      }
    }

    setGlobalCooldownForAll(endTime) {
      if (!this.globalCooldowns) {
        this.globalCooldowns = {
          [AbilityCategory.UTILITY]: 0,
          [AbilityCategory.MOVEMENT]: 0
        };
      }
      for (const category of Object.values(AbilityCategory)) {
        this.globalCooldowns[category] = endTime;
      }
    }

    resetGlobalCooldowns() {
      this.setGlobalCooldownForAll(0);
    }

    getGlobalCooldownEnd(category) {
      if (!category) return this.getMaxGlobalCooldownEnd();
      return this.globalCooldowns?.[category] || 0;
    }

    getMaxGlobalCooldownEnd() {
      if (!this.globalCooldowns) {
        return 0;
      }
      return Math.max(...Object.values(this.globalCooldowns));
    }

    getGlobalCooldownSnapshot() {
      if (!this.globalCooldowns) {
        return {
          [AbilityCategory.UTILITY]: 0,
          [AbilityCategory.MOVEMENT]: 0
        };
      }
      return {
        [AbilityCategory.UTILITY]: this.globalCooldowns[AbilityCategory.UTILITY] || 0,
        [AbilityCategory.MOVEMENT]: this.globalCooldowns[AbilityCategory.MOVEMENT] || 0
      };
    }

    /**
     * Start an active effect (timed ability)
     * @param {string} abilityId
     * @param {number} duration
     * @param {number} timestamp
     * @param {number} stacks
     */
    startEffect(abilityId, duration, timestamp, stacks) {
      this.activeEffects.set(abilityId, {
        startTime: timestamp,
        endTime: timestamp + duration,
        stacks
      });
    }

    /**
     * Check if an effect is currently active
     * @param {string} abilityId
     * @param {number} timestamp
     * @returns {boolean}
     */
    isEffectActive(abilityId, timestamp) {
      const effect = this.activeEffects.get(abilityId);
      if (!effect) return false;
      return timestamp < effect.endTime;
    }

    /**
     * Get active effect info
     * @param {string} abilityId
     * @returns {Object|null}
     */
    getActiveEffect(abilityId) {
      return this.activeEffects.get(abilityId) || null;
    }

    /**
     * End an active effect
     * @param {string} abilityId
     */
    endEffect(abilityId) {
      this.activeEffects.delete(abilityId);
    }

    /**
     * Get all expired effects
     * @param {number} timestamp
     * @returns {string[]} - Array of ability IDs
     */
    getExpiredEffects(timestamp) {
      const expired = [];
      for (const [abilityId, effect] of this.activeEffects) {
        if (timestamp >= effect.endTime) {
          expired.push(abilityId);
        }
      }
      return expired;
    }

    /**
     * Clear all ability state (on death/respawn)
     */
    clear() {
      this.equipped = {
        [AbilityCategory.UTILITY]: null,
        [AbilityCategory.MOVEMENT]: null
      };
      this.cooldowns.clear();
      this.resetGlobalCooldowns();
      this.activeEffects.clear();
    }

    /**
     * Drop all equipped abilities (returns array for loot spawning)
     * @returns {Object[]} - Array of { abilityId, stacks, category }
     */
    dropAll() {
      const dropped = [];
      for (const [category, slot] of Object.entries(this.equipped)) {
        if (slot) {
          dropped.push({
            abilityId: slot.abilityId,
            stacks: slot.stacks,
            category
          });
        }
      }
      this.clear();
      return dropped;
    }

    /**
     * Serialize for network transmission
     */
    serialize() {
      const cooldowns = {};
      for (const [id, end] of this.cooldowns) {
        cooldowns[id] = end;
      }

      const activeEffects = {};
      for (const [id, effect] of this.activeEffects) {
        activeEffects[id] = effect;
      }

      return {
        equipped: { ...this.equipped },
        cooldowns,
        globalCooldowns: this.getGlobalCooldownSnapshot(),
        activeEffects,
        silencedUntil: this.silencedUntil || 0
      };
    }

    /**
     * Restore from serialized data
     * @param {Object} data
     */
    fromData(data) {
      if (data.equipped) {
        this.equipped = { ...data.equipped };
      }
      if (data.cooldowns) {
        this.cooldowns = new Map(Object.entries(data.cooldowns));
      }
      if (data.globalCooldowns) {
        this.globalCooldowns = {
          [AbilityCategory.UTILITY]: data.globalCooldowns[AbilityCategory.UTILITY] || 0,
          [AbilityCategory.MOVEMENT]: data.globalCooldowns[AbilityCategory.MOVEMENT] || 0
        };
      } else if (data.globalCooldownEnd) {
        // Backward compatibility
        this.setGlobalCooldownForAll(data.globalCooldownEnd);
      }
      if (data.activeEffects) {
        this.activeEffects = new Map(Object.entries(data.activeEffects));
      }
      this.silencedUntil = data.silencedUntil || 0;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // ABILITY REGISTRY
  // ─────────────────────────────────────────────────────────────
  class AbilityRegistry {
    constructor() {
      this.abilities = new Map();
      this.byCategory = new Map();

      // Initialize category maps
      for (const category of Object.values(AbilityCategory)) {
        this.byCategory.set(category, new Map());
      }
    }

    /**
     * Register an ability
     * Uses concrete ability classes when available for polymorphic behavior
     * @param {Ability|Object} abilityOrConfig
     * @returns {boolean}
     */
    register(abilityOrConfig) {
      let ability;

      // If already an Ability instance, use it directly
      if (abilityOrConfig instanceof Ability) {
        ability = abilityOrConfig;
      }
      // A factory installed by the server (setAbilityFactory) builds the
      // concrete subclass; the client never installs one and gets base Ability
      else if (abilityFactory) {
        ability = abilityFactory(abilityOrConfig);
      }
      // Fallback to base Ability class (client-side uses this)
      else {
        ability = new Ability(abilityOrConfig);
      }

      this.abilities.set(ability.id, ability);

      // Add to category index
      const categoryMap = this.byCategory.get(ability.category);
      if (categoryMap) {
        categoryMap.set(ability.id, ability);
      }

      return true;
    }

    /**
     * Register multiple abilities from JSON data
     * @param {Object[]} abilityConfigs
     */
    registerBulk(abilityConfigs) {
      for (const config of abilityConfigs) {
        this.register(config);
      }
    }

    /**
     * Get ability by ID
     * @param {string} abilityId
     * @returns {Ability|null}
     */
    get(abilityId) {
      return this.abilities.get(abilityId) || null;
    }

    /**
     * Check if ability exists
     * @param {string} abilityId
     * @returns {boolean}
     */
    has(abilityId) {
      return this.abilities.has(abilityId);
    }

    /**
     * Get all abilities
     * @returns {Ability[]}
     */
    getAll() {
      return Array.from(this.abilities.values());
    }

    /**
     * Export all ability configs for client synchronization
     * Returns only client-needed data (no server-side logic)
     * @returns {Array<Object>} Array of ability configs for client use
     */
    getAllConfigs() {
      return this.getAll().map(ability => ({
        id: ability.id,
        name: ability.name,
        description: ability.description,
        category: ability.category,
        rarity: ability.rarity,
        icon: ability.icon,
        color: ability.color,
        cooldown: ability.cooldown,
        duration: ability.duration,
        maxStacks: ability.maxStacks,
        canShoot: ability.canShoot,
        canReload: ability.canReload
      }));
    }

    /**
     * Get abilities by category
     * @param {string} category
     * @returns {Ability[]}
     */
    getByCategory(category) {
      const categoryMap = this.byCategory.get(category);
      return categoryMap ? Array.from(categoryMap.values()) : [];
    }

    /**
     * Get abilities by rarity
     * @param {string} rarity
     * @returns {Ability[]}
     */
    getByRarity(rarity) {
      return this.getAll().filter(a => a.rarity === rarity);
    }

    /**
     * Get a random ability using weighted selection
     * @param {Object} filters - { category?, rarity? }
     * @returns {Ability|null}
     */
    getRandomWeighted(filters = {}) {
      let candidates = this.getAll();

      if (filters.category) {
        candidates = candidates.filter(a => a.category === filters.category);
      }
      if (filters.rarity) {
        candidates = candidates.filter(a => a.rarity === filters.rarity);
      }

      if (candidates.length === 0) return null;

      const totalWeight = candidates.reduce((sum, a) => sum + a.spawnWeight, 0);
      let random = Math.random() * totalWeight;

      for (const ability of candidates) {
        random -= ability.spawnWeight;
        if (random <= 0) return ability;
      }

      return candidates[candidates.length - 1];
    }

    /**
     * Get all ability IDs
     * @returns {string[]}
     */
    getAllIds() {
      return Array.from(this.abilities.keys());
    }
  }

  // ─────────────────────────────────────────────────────────────
  // ABILITY PICKUP (spawned in world)
  // ─────────────────────────────────────────────────────────────
  class AbilityPickup {
    constructor(abilityId, x, y, stacks = 1, id = null) {
      this.id = id || `ability_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      this.abilityId = abilityId;
      this.x = x;
      this.y = y;
      this.stacks = stacks;
      this.spawnTime = Date.now();
      this.collected = false;
      this.collectedBy = null;
    }

    /**
     * Check if player can collect this pickup
     * @param {Object} player
     * @param {number} collectRadius
     * @returns {boolean}
     */
    canCollect(player, collectRadius = 40) {
      if (this.collected) return false;
      if (!player.isAlive || (typeof player.isAlive === 'function' && !player.isAlive())) {
        return false;
      }

      const dx = this.x - player.x;
      const dy = this.y - player.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      return dist <= collectRadius;
    }

    /**
     * Mark as collected
     * @param {string} playerId
     */
    collect(playerId) {
      this.collected = true;
      this.collectedBy = playerId;
    }

    /**
     * Serialize for network transmission
     */
    serialize() {
      return {
        id: this.id,
        abilityId: this.abilityId,
        x: this.x,
        y: this.y,
        stacks: this.stacks,
        collected: this.collected
      };
    }
  }

  // ─────────────────────────────────────────────────────────────
  // SINGLETON INSTANCE
  // ─────────────────────────────────────────────────────────────
  // Population is the environment's job (see WeaponRegistry.js): the server
  // registers config/abilities.json entries at boot, the client after fetching
  // /config/abilities.json.
  const abilityRegistry = new AbilityRegistry();

export {
  // Enums
  AbilityCategory,
  AbilitySlots,
  AbilityRarity,
  RarityColors,
  // Classes
  Ability,
  PlayerAbilityState,
  AbilityRegistry,
  AbilityPickup,
  // Singleton
  abilityRegistry
};
